import * as THREE from "three";
import { ModelLoader, Scene3D } from "./core";
import { InputHandler } from "./features";
import {
  darkColors,
  EngineIncomingMessage,
  EngineIncomingMessageType,
  EngineInitParams,
  EngineStyleTheme,
  lightColors,
  ColorPalette,
  BodyType,
} from "@bennara/shared";
import { WebViewBridge } from "./bridge/WebViewBridge";
import {
  findObjectByLocation,
  getLocationFromObject,
} from "./utils/modelGroup";
import { PointMarkers } from "./features/PointMarkers";

const TAP_HOLD_MS = 300;

type HeldTap = {
  clientX: number;
  clientY: number;
  hit: THREE.Intersection | null;
  timer: ReturnType<typeof setTimeout>;
};

export class Engine {
  private theme: EngineStyleTheme = "light";
  private colors: ColorPalette = lightColors;
  private heldTap: HeldTap | null = null;

  private readonly scene: Scene3D;
  private readonly modelLoader = new ModelLoader();

  private readonly pointMarkers: PointMarkers;

  private readonly inputHandler: InputHandler;
  private model: THREE.Group | null = null;
  private animationId: number | null = null;
  /** Zoom requested before the point existed. Applied on the next SET_POINTS. */
  private pendingZoomPointId: string | null = null;

  private readonly bridge = new WebViewBridge();
  private readonly loadingOverlay: HTMLDivElement;

  constructor(container: HTMLElement, init: EngineInitParams) {
    this.theme = init.theme;
    this.colors = this.theme === "light" ? lightColors : darkColors;

    this.scene = new Scene3D(container, this.colors);
    this.loadingOverlay = this._createLoadingOverlay(container);
    this.pointMarkers = new PointMarkers(this.scene.scene);

    this.inputHandler = new InputHandler(
      this.scene.canvas,
      this.scene.camera,
      (x, y) => this.handleTap(x, y),
    );

    this.bridge.onMessage((message) => this._handleBridgeMessage(message));

    this._loadModel(init.bodyType);
    this._startAnimationLoop();
    this._bindResize();
  }

  private _changeTheme(theme: EngineStyleTheme): void {
    this.theme = theme;
    this.colors = theme === "light" ? lightColors : darkColors;
    window.colors = this.colors;

    this.scene.changeTheme(this.colors);
    this.pointMarkers.applyTheme();
  }

  private _createLoadingOverlay(container: HTMLElement): HTMLDivElement {
    const overlay = document.createElement("div");
    overlay.className = "engine-loading-overlay";
    overlay.style.backgroundColor = this.colors.background;
    overlay.style.color = this.colors.textPrimary;
    const spinner = document.createElement("div");
    spinner.className = "engine-loading-spinner";
    overlay.appendChild(spinner);
    container.appendChild(overlay);
    return overlay;
  }

  private _removeLoadingOverlay(): void {
    this.loadingOverlay.remove();
  }

  private _loadModel(bodyType: BodyType = BodyType.BODY_TYPE_1): void {
    this.modelLoader.load(
      bodyType,
      (model) => {
        this.model = model;
        this.scene.scene.add(model);
        this.scene.setBodyReferenceFromModel(model);
        this.pointMarkers.setBody(model);
        this._onModelReady();

        this._removeLoadingOverlay();
        this.bridge.postEngineReady();
      },
      () => {
        this._removeLoadingOverlay();
        this.bridge.postErrorMessage("Failed to load model");
      },
    );
  }

  private _onModelReady(): void {
    this.inputHandler.bindWindowEvents();
  }

  private _startAnimationLoop(): void {
    const loop = () => {
      this.animationId = requestAnimationFrame(loop);
      this.pointMarkers.setPointScale(this.scene.getZoom(), this.scene.camera);
      this.scene.render();
    };
    loop();
  }

  private _bindResize(): void {
    window.addEventListener("resize", this.scene.resize);
  }

  private clearHeldTap(): void {
    if (!this.heldTap) return;
    clearTimeout(this.heldTap.timer);
    this.heldTap = null;
  }

  private dispatchTap(
    clientX: number,
    clientY: number,
    hit: THREE.Intersection | null,
  ): void {
    if (!hit) {
      this.bridge.postTapOutside({
        coordinates: {
          x: clientX,
          y: clientY,
          z: 0,
        },
      });
      return;
    }

    const pointHit = hit.object.userData.pointId;

    if (pointHit) {
      this.bridge.postTapOnPoint({
        pointId: pointHit,
        location: {
          coordinates: {
            x: hit.point.x,
            y: hit.point.y,
            z: hit.point.z,
          },
          location: getLocationFromObject(hit.object),
        },
      });
      return;
    }

    this.bridge.postTapOnBody({
      location: {
        coordinates: {
          x: hit.point.x,
          y: hit.point.y,
          z: hit.point.z,
        },
        location: getLocationFromObject(hit.object),
      },
    });
  }

  private handleTap(clientX: number, clientY: number): void {
    const hit =
      this.inputHandler.raycastFirst(clientX, clientY, this.scene.scene) ??
      null;

    if (this.heldTap) {
      this.clearHeldTap();
      this.bridge.postDoubleTapOnBody();
      return;
    }

    const timer = setTimeout(() => {
      const pending = this.heldTap;
      if (!pending || pending.timer !== timer) return;
      this.heldTap = null;
      this.dispatchTap(pending.clientX, pending.clientY, pending.hit);
    }, TAP_HOLD_MS);

    this.heldTap = { clientX, clientY, hit, timer };
  }

  private zoomToPoint(pointId: string, preserveZoom: boolean) {
    const point = this.pointMarkers.getPoint(pointId);
    if (!point) {
      this.pendingZoomPointId = pointId;
      return;
    }

    this.pendingZoomPointId = null;
    const center = new THREE.Vector3(
      point.coordinates.x,
      point.coordinates.y,
      point.coordinates.z,
    );
    this.scene.centerOnPoint(center, preserveZoom ? { preserveZoom: true } : undefined);
  }

  private _handleBridgeMessage(message: EngineIncomingMessage): void {
    switch (message.type) {
      case EngineIncomingMessageType.SET_THEME:
        this._changeTheme(message.payload.theme);
        break;
      case EngineIncomingMessageType.SET_MARKER_STYLE:
        this.pointMarkers.setMarkerStyle(message.payload.style);
        break;
      case EngineIncomingMessageType.SET_POINTS:
        this.pointMarkers.setPoints(message.payload.points);
        if (this.pendingZoomPointId) {
          const pendingId = this.pendingZoomPointId;
          this.pendingZoomPointId = null;
          this.zoomToPoint(pendingId, false);
        }
        break;
      case EngineIncomingMessageType.SET_ENGINE_STATE:
        this.pointMarkers.updateEngineState(message.payload.state);
        break;
      case EngineIncomingMessageType.ZOOM_IN_LOCATION:
        if (!this.model) return;

        const group = findObjectByLocation(
          this.model,
          message.payload.location,
        );

        if (group) {
          if (message.payload.preserveZoom) {
            this.scene.centerOnGroup(group);
          } else {
            this.scene.centerOnGroup(group);
          }
        }
        break;
      case EngineIncomingMessageType.ZOOM_IN_POINT:
        this.zoomToPoint(message.payload.pointId, Boolean(message.payload.preserveZoom));
        break;
      case EngineIncomingMessageType.RESET_CAMERA:
        this.scene.resetCameraToInitial();
        break;
    }
  }
}
